import { CheckIcon, Loader2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { AiMarkdown } from '@/platform/components/ai-markdown';

/** Shown in order while a KI answer is still running. */
const AI_WAIT_STEPS = [
  'Frage ist raus.',
  'Suche in den freigegebenen Texten.',
  'Schreibe die Antwort.',
] as const;

/** Wait steps plus the answer as Markdown, including a partial text that is still arriving. */
export function AiLiveReply({
  active,
  text,
  step,
}: {
  active: boolean;
  text: string;
  step?: number;
}) {
  const auto = useWaitStep(active && step === undefined);
  const writing = text.trim().length > 0;
  const current = writing
    ? AI_WAIT_STEPS.length - 1
    : Math.min(Math.max(step ?? auto, 0), AI_WAIT_STEPS.length - 1);
  if (!active && !writing) {
    return null;
  }
  return (
    <div className="space-y-3 rounded-xl border bg-card px-4 py-3 shadow-sm">
      {active && !writing ? (
        <ol className="space-y-2">
          {AI_WAIT_STEPS.map((line, index) => {
            const state = index < current ? 'done' : index === current ? 'current' : 'waiting';
            return (
              <li
                key={line}
                role={state === 'current' ? 'status' : undefined}
                className={
                  state === 'waiting'
                    ? 'flex items-start gap-2 text-sm text-muted-foreground/70'
                    : 'flex items-start gap-2 text-sm text-foreground'
                }
              >
                {state === 'current' ? (
                  <Loader2Icon className="mt-0.5 size-4 shrink-0 animate-spin" aria-hidden />
                ) : state === 'done' ? (
                  <CheckIcon className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                ) : (
                  <span className="mt-1 size-4 shrink-0 rounded-full border border-border" />
                )}
                <span className={state === 'current' ? 'font-medium' : undefined}>{line}</span>
              </li>
            );
          })}
        </ol>
      ) : null}
      {active && writing ? (
        <p role="status" className="flex items-start gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="mt-0.5 size-4 shrink-0 animate-spin" aria-hidden />
          <span>{AI_WAIT_STEPS[AI_WAIT_STEPS.length - 1]}</span>
        </p>
      ) : null}
      {writing ? <AiMarkdown text={text} /> : null}
    </div>
  );
}

function useWaitStep(active: boolean): number {
  const [clock, setClock] = useState({ on: false, tick: 0 });
  if (clock.on !== active) {
    setClock({ on: active, tick: 0 });
  }
  useEffect(() => {
    if (!active) {
      return;
    }
    const timer = window.setInterval(() => {
      setClock((current) =>
        current.on
          ? { on: true, tick: Math.min(current.tick + 1, AI_WAIT_STEPS.length - 1) }
          : current,
      );
    }, 4000);
    return () => window.clearInterval(timer);
  }, [active]);
  return active ? clock.tick : 0;
}
